import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Card, ErrorNotice, Icon, StatusPill, type Status } from '@hive/ui';
import { api, errorMessage, type StreamProbe, type StreamSetup } from '../api/client';
import { useLoad, useNow } from '../api/hooks';
import { Dialog } from '../components/Dialog';
import { Shell } from '../components/Shell';
import { AD_RIGHTS, CONTRIBUTION, emptyStream, SCTE35, StreamFields, type StreamFormState } from '../components/StreamForm';
import { ago } from '../format';

export const STREAM_STATUS: Record<StreamSetup['status'], { label: string; status: Status }> = {
  draft: { label: 'Draft', status: 'idle' },
  tested: { label: 'Tested', status: 'promo' },
  published: { label: 'Published', status: 'healthy' },
};

export function ladder(p?: StreamProbe): string {
  if (!p?.renditions?.length) return '—';
  const top = p.renditions[0];
  return `${top.height ? `${top.height}p · ` : ''}${(top.bandwidthKbps / 1000).toFixed(1)} Mbps top · ${p.renditions.length} rendition${p.renditions.length === 1 ? '' : 's'}`;
}

/** Streams from the origin: what venues can be offered, and what is still being tested. */
export function StreamsPage() {
  const navigate = useNavigate();
  const now = useNow(30_000);
  const streams = useLoad(async () => (await api.GET('/v1/setup/streams')).data);
  const [adding, setAdding] = useState(false);

  return (
    <Shell network={null} live>
      <main className="mc-page">
        <div className="mc-pagehead">
          <div className="mc-pagehead__left">
            <h1 className="mc-h1">Streams</h1>
            <span className="mc-pill-soft">Clear DASH and HLS from the origin · venues see published streams only</span>
          </div>
          <div className="mc-pagehead__actions">
            <Button variant="primary" onClick={() => setAdding(true)}>
              <Icon name="plus" size={18} />
              Add stream
            </Button>
          </div>
        </div>
        <Card className="mc-table-card">
          <table className="mc-table">
            <thead>
              <tr>
                <th scope="col">Stream</th>
                <th scope="col">Status</th>
                <th scope="col">Last probe</th>
                <th scope="col">Ads</th>
                <th scope="col">In use</th>
              </tr>
            </thead>
            <tbody>
              {(streams.data ?? []).map((s) => (
                <tr key={s.id}>
                  <td>
                    <Link to={`/streams/${s.id}`} className="mc-table__link">
                      <span className="mc-table__who">
                        <span className="mc-strong">
                          {s.name} <span className="mc-pill-soft">{s.streamType.toUpperCase()}</span>
                        </span>
                        <span className="mc-small hv-muted">
                          {CONTRIBUTION[s.contribution].label} · {s.code ?? 'no feed code'}
                          {s.backupUrl ? ' · has backup' : ''}
                        </span>
                      </span>
                    </Link>
                  </td>
                  <td>
                    <StatusPill status={STREAM_STATUS[s.status].status}>{STREAM_STATUS[s.status].label}</StatusPill>
                  </td>
                  <td className="mc-small">
                    {s.probe ? (
                      <>
                        <span className={s.probe.ok ? 'mc-good' : 'mc-bad'}>{s.probe.ok ? ladder(s.probe) : s.probe.error}</span>
                        <span className="hv-muted mc-line">
                          {ago(s.probe.at, now)} ago{s.probe.live ? ' · live' : ''}
                          {s.probe.warnings?.length ? ` · ${s.probe.warnings.length} note${s.probe.warnings.length === 1 ? '' : 's'}` : ''}
                        </span>
                      </>
                    ) : (
                      <span className="hv-muted">Not probed</span>
                    )}
                  </td>
                  <td className="mc-small">
                    {AD_RIGHTS[s.adRights].label}
                    <span className="hv-muted mc-line">{SCTE35[s.scte35]}</span>
                  </td>
                  <td className="mc-small">
                    {s.screensPlaying} screen{s.screensPlaying === 1 ? '' : 's'}
                    <span className="hv-muted mc-line">
                      {s.contribution === 'channel'
                        ? `${s.venuesWithRights} venue${s.venuesWithRights === 1 ? '' : 's'} with rights`
                        : `${s.fixtures} fixture${s.fixtures === 1 ? '' : 's'} and loops`}
                    </span>
                  </td>
                </tr>
              ))}
              {streams.data && streams.data.length === 0 && (
                <tr>
                  <td colSpan={5} className="mc-table__empty">
                    No streams yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Card>
      </main>
      {adding && <AddStreamDialog onClose={() => setAdding(false)} onCreated={(id) => navigate(`/streams/${id}`)} />}
    </Shell>
  );
}

function AddStreamDialog({ onClose, onCreated }: { onClose(): void; onCreated(id: string): void }) {
  const [form, setForm] = useState<StreamFormState>(emptyStream);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { data, error: err } = await api.POST('/v1/setup/streams', {
      body: {
        name: form.name.trim(),
        streamUrl: form.streamUrl.trim(),
        streamType: form.streamType,
        contribution: form.contribution,
        adRights: form.adRights,
        scte35: form.scte35,
        ...(form.code.trim() ? { code: form.code.trim() } : {}),
        ...(form.description.trim() ? { description: form.description.trim() } : {}),
        ...(form.backupUrl.trim() ? { backupUrl: form.backupUrl.trim() } : {}),
      },
    });
    if (!data) {
      setBusy(false);
      return setError(errorMessage(err));
    }
    // Probe straight away, so the stream page opens with a report.
    await api.POST('/v1/setup/streams/{streamId}/probe', { params: { path: { streamId: data.id } } });
    setBusy(false);
    onCreated(data.id);
  }

  return (
    <Dialog
      titleId="add-stream-title"
      title="Add a stream"
      subtitle="It starts as a draft; test it, then publish it to venues."
      onClose={onClose}
      width={640}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form="add-stream-form" disabled={busy}>
            {busy ? 'Adding and probing…' : 'Add and probe'}
          </Button>
        </>
      }
    >
      <form id="add-stream-form" onSubmit={submit} className="mc-form">
        {error && <ErrorNotice title={error} />}
        <StreamFields form={form} onChange={setForm} />
      </form>
    </Dialog>
  );
}
